import React from 'react';
import { useNavigate } from 'react-router-dom';
import { reducedModeEnabled } from '../utils/reducedMode';

const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="bg-white/5 rounded-2xl border border-white/10 p-6 md:p-8">
    <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">{title}</h2>
    <div className="prose prose-invert max-w-none text-gray-200">
      {children}
    </div>
  </section>
);

const VideoPlaceholder: React.FC<{ label: string }> = ({ label }) => (
  <div className="bg-black/70 rounded-lg border border-white/10 aspect-video flex items-center justify-center text-gray-400">
    <div className="text-center">
      <div className="text-4xl mb-2">🎬</div>
      <div className="text-sm">{label} (video coming soon)</div>
    </div>
  </div>
);

const InstructionsPage: React.FC = () => {
  const navigate = useNavigate();
  

  return (
    <div className="min-h-screen bg-gradient-to-br from-qr-dark via-gray-900 to-qr-dark text-white">
      <header className="p-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/Qroke Logo.png" alt="QrOke Logo" className="w-10 h-10 rounded-lg" />
            <span className="text-white font-display font-bold text-2xl">QrOke</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="px-3 py-2 rounded-lg border border-white/20 text-gray-200">Home</button>
            <button onClick={() => navigate('/dashboard')} className="px-3 py-2 rounded-lg bg-qr-primary hover:bg-qr-secondary text-white">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 md:p-8 space-y-8">
        <div className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10">
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-3">Welcome to QrOke</h1>
          <p className="text-gray-300 leading-relaxed">
            {reducedModeEnabled
              ? 'This guide covers the fast QrOke flow: start a party, share the QR code, search or browse songs, and keep the queue moving.'
              : 'This guide walks you through setting up QrOke, hosting your first party, and mastering host controls and external displays. We’ll add short YouTube tutorials here soon.'}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6 text-sm">
            <a href="#setup" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">1. Quick Setup</a>
            <a href="#hosting" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">2. Start a Party</a>
            <a href="#search" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">3. Search & Requests</a>
            <a href="#queue" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">4. Queue Flow</a>
            <a href="#external-display" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">5. Playback Display</a>
            {reducedModeEnabled ? (
              <a href="#lean-mode" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">6. Core Hosting Flow</a>
            ) : (
              <a href="#audio" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">6. Audio & Latency</a>
            )}
            {!reducedModeEnabled && <a href="#branding" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">7. Custom Branding</a>}
            {!reducedModeEnabled && <a href="#venue-ads" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">8. Venue Ads</a>}
            {!reducedModeEnabled && <a href="#billing" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">9. Party Pass</a>}
            <a href="#troubleshoot" className="bg-white/10 hover:bg-white/15 rounded-lg px-3 py-2 border border-white/10">{reducedModeEnabled ? '7. Troubleshooting' : '10. Troubleshooting'}</a>
          </div>
        </div>

        <Section id="setup" title="Quick Setup">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-disc pl-5 space-y-2">
              <li>Use a laptop or desktop for the host device (Chrome recommended).</li>
              <li>Connect to reliable Wi‑Fi; avoid cellular hotspots when possible.</li>
              <li>Sign in and open your <strong>Dashboard</strong>.</li>
              <li>Optional: Prepare an HDMI/Chromecast/AirPlay display for lyrics.</li>
            </ul>
            <VideoPlaceholder label="Account & environment setup" />
          </div>
        </Section>

        <Section id="hosting" title="Starting a Party">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-decimal pl-5 space-y-2">
              <li>From Home or Dashboard, click <strong>Start Party</strong>.</li>
              <li>Share the QR code on screen so guests can request songs.</li>
              <li>Use the <strong>Queue</strong> tab to manage up‑next songs and simple splash timing.</li>
            </ul>
            <VideoPlaceholder label="Create party & share QR" />
          </div>
        </Section>

        <Section id="search" title="Search & Song Requests">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-disc pl-5 space-y-2">
              <li>Guests join with the party code or QR link and enter their singer name before queueing.</li>
              <li>If browser YouTube search is configured, guests can run lightweight karaoke search directly from the request page.</li>
              <li>If YouTube search is unavailable, the host can still publish a local catalog for guests to browse.</li>
            </ul>
            <VideoPlaceholder label="Search and request flow" />
          </div>
        </Section>

        <Section id="queue" title="Queue & Playback Flow">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-disc pl-5 space-y-2">
              <li>Queue songs from the request page and keep the next singer visible.</li>
              <li>The splash screen handles simple transitions between songs without extra setup.</li>
              <li>When a song finishes, the party advances to the next queued item automatically.</li>
            </ul>
            <VideoPlaceholder label="Queue management and playback" />
          </div>
        </Section>

        <Section id="external-display" title="Playback Display">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-disc pl-5 space-y-2">
              <li>Open the party on the screen you want to use for playback, or keep the host screen as the main display.</li>
              <li>Keep the display awake and disable sleep or screen savers during a session.</li>
              <li>Use the QR code and splash screen on that display so guests always know how to join and what is next.</li>
            </ul>
            <VideoPlaceholder label="Playback display walkthrough" />
          </div>
        </Section>

        {reducedModeEnabled ? (
          <Section id="lean-mode" title="Core Hosting Flow">
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div>
                <p className="mb-3">The current build is focused on hosting a working karaoke night with the smallest reliable surface area.</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Party launch, guest join, queueing, and lightweight search are the core path.</li>
                  <li>Use the local catalog or browser YouTube search, depending on how the host has the party set up.</li>
                  <li>Use the feedback button if something important is missing for your event.</li>
                </ul>
              </div>
              <VideoPlaceholder label="Core hosting overview" />
            </div>
          </Section>
        ) : (
          <>
            <Section id="audio" title="Audio Setup & Latency">
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <ul className="list-disc pl-5 space-y-2">
                  <li>Select your audio output device in Host Controls.</li>
                  <li>Use the host device’s built‑in or connected microphones and set levels/effects (EQ, reverb, echo, gate) in Host Controls.</li>
                  <li>Wired speakers or low‑latency Bluetooth help reduce echo and delay.</li>
                </ul>
                <VideoPlaceholder label="Audio routing & latency tips" />
              </div>
            </Section>

            <Section id="billing" title="Party Pass & Subscriptions">
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Party Pass ($2)</strong> removes ads, unlocks unlimited queue, custom branding, and venue ads for a 12-hour session. Tax inclusive.</li>
                  <li><strong>Party Pass Monthly ($5/month)</strong> gives you unlimited 12-hour sessions with all Party Pass features.</li>
                  <li>Subscriptions for pros/venues are available—contact us for tailored setups.</li>
                </ul>
                <VideoPlaceholder label="Party Pass overview" />
              </div>
            </Section>

            <Section id="branding" title="Custom Branding">
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <div>
                  <p className="mb-3">Available to all <strong>Party Pass</strong> and <strong>Monthly</strong> subscribers. Personalize your karaoke experience with your own branding on splash screens.</p>
                  <ul className="list-decimal pl-5 space-y-2">
                    <li>Go to your <strong>Dashboard</strong> and click the <strong>🎨 Branding</strong> button.</li>
                    <li>Toggle branding <strong>On</strong> with the enable switch.</li>
                    <li>Enter your <strong>venue name</strong> and optional <strong>splash message</strong>.</li>
                    <li>Add URLs for your <strong>logo</strong>, <strong>background image</strong>, and optional <strong>watermark</strong>.</li>
                    <li>Pick your <strong>primary and secondary colors</strong> using the color pickers.</li>
                    <li>Choose a <strong>font</strong> that matches your venue’s style.</li>
                    <li>Check the <strong>live preview</strong> and click <strong>Save</strong>.</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-400">Tip: Use high-resolution images (1920×1080+) for backgrounds. PNG logos with transparent backgrounds look best.</p>
                </div>
                <VideoPlaceholder label="Custom branding setup" />
              </div>
            </Section>

            <Section id="venue-ads" title="Venue Ads">
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <div>
                  <p className="mb-3">Play your own image or video ads between songs. Available to <strong>Party Pass</strong> and <strong>Monthly</strong> subscribers.</p>
                  <ul className="list-decimal pl-5 space-y-2">
                    <li>From the <strong>Dashboard</strong>, click the <strong>📺 Venue Ads</strong> button.</li>
                    <li>Add ads by providing an <strong>image URL</strong> or <strong>YouTube video ID</strong>.</li>
                    <li>Set the <strong>display duration</strong> and <strong>rotation order</strong>.</li>
                    <li>Ads will play automatically between songs during your party.</li>
                  </ul>
                  <p className="mt-3 text-sm text-gray-400">Tip: Great for promoting drink specials, upcoming events, or sponsor messages.</p>
                </div>
                <VideoPlaceholder label="Venue ads walkthrough" />
              </div>
            </Section>
          </>
        )}

        <Section id="troubleshoot" title="Troubleshooting">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <ul className="list-disc pl-5 space-y-2">
              <li>If sign‑in fails on mobile, open in Safari/Chrome (avoid in‑app browsers).</li>
              <li>If guest search is empty, confirm reduced-mode YouTube search is configured or publish a local catalog.</li>
              <li>If playback seems delayed, prefer wired speakers or low‑latency Bluetooth.</li>
            </ul>
            <VideoPlaceholder label="Common issues & fixes" />
          </div>
        </Section>
      </main>

      <footer className="py-12 px-6 border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center text-gray-300 text-sm mb-2">
            <a href="https://qroke.org/terms-of-service" target="_blank" rel="noopener" className="hover:text-white">Terms of Service</a>
            <span className="hidden sm:inline">•</span>
            <a href="https://qroke.org/privacy-policy-for-qroke" target="_blank" rel="noopener" className="hover:text-white">Privacy Policy</a>
            <span className="hidden sm:inline">•</span>
            <button onClick={() => navigate('/content-sources')} className="hover:text-white">Content Sources & Licensing</button>
          </div>
          <div className="text-gray-400">© 2025 QrOke — More tutorials coming soon.</div>
        </div>
      </footer>
    </div>
  );
};

export default InstructionsPage;
